/**
 * Wishlist JavaScript
 * localStorage-based wishlist functionality
 */

(function() {
  'use strict';

  const WISHLIST_KEY = 'designer_bags_wishlist';
  const WISHLIST_DATA_KEY = 'designer_bags_wishlist_data';

  /**
   * Initialize wishlist functionality
   */
  function init() {
    bindButtons();
    updateCounts();
    updateButtonStates();
  }

  /**
   * Get wishlist items (array of product handles)
   */
  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  /**
   * Get wishlist with product data
   */
  function getWishlistData() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_DATA_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  /**
   * Save wishlist
   */
  function saveWishlist(handles) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    updateCounts();
    dispatchEvent('wishlistUpdate', { handles });
  }

  /**
   * Save product data to wishlist
   */
  function saveProductData(handle, data) {
    const allData = getWishlistData();
    allData[handle] = data;
    localStorage.setItem(WISHLIST_DATA_KEY, JSON.stringify(allData));
  }

  /**
   * Remove product data from wishlist
   */
  function removeProductData(handle) {
    const allData = getWishlistData();
    delete allData[handle];
    localStorage.setItem(WISHLIST_DATA_KEY, JSON.stringify(allData));
  }

  /**
   * Add product to wishlist
   */
  function addToWishlist(handle, productData = null) {
    const wishlist = getWishlist();

    if (!wishlist.includes(handle)) {
      wishlist.push(handle);
      saveWishlist(wishlist);

      if (productData) {
        saveProductData(handle, productData);
      }

      showToast('Added to wishlist');
      dispatchEvent('wishlistAdd', { handle });
    }

    updateButtonState(handle, true);
  }

  /**
   * Remove product from wishlist
   */
  function removeFromWishlist(handle) {
    let wishlist = getWishlist();
    wishlist = wishlist.filter(h => h !== handle);
    saveWishlist(wishlist);
    removeProductData(handle);

    showToast('Removed from wishlist');
    dispatchEvent('wishlistRemove', { handle });
    updateButtonState(handle, false);
  }

  /**
   * Toggle product in wishlist
   */
  function toggleWishlist(handle, productData = null) {
    if (isInWishlist(handle)) {
      removeFromWishlist(handle);
    } else {
      addToWishlist(handle, productData);
    }
  }

  /**
   * Check if product is in wishlist
   */
  function isInWishlist(handle) {
    return getWishlist().includes(handle);
  }

  /**
   * Get wishlist count
   */
  function getCount() {
    return getWishlist().length;
  }

  /**
   * Clear entire wishlist
   */
  function clearWishlist() {
    localStorage.removeItem(WISHLIST_KEY);
    localStorage.removeItem(WISHLIST_DATA_KEY);
    updateCounts();
    updateButtonStates();
    dispatchEvent('wishlistClear', {});
  }

  /**
   * Bind click handlers to wishlist buttons (only once)
   */
  let buttonsBound = false;
  function bindButtons() {
    if (buttonsBound) return;
    buttonsBound = true;

    document.addEventListener('click', function(e) {
      const btn = e.target.closest('[data-wishlist-add]');
      if (!btn) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const handle = btn.dataset.wishlistAdd;
      if (!handle) return;

      // Get product data from button or parent
      const productData = getProductDataFromButton(btn);

      toggleWishlist(handle, productData);
    });
  }

  /**
   * Extract product data from button context
   */
  function getProductDataFromButton(btn) {
    const handle = btn.dataset.wishlistAdd;

    // Try product card context (collection grid)
    const card = btn.closest('[data-product-handle]');
    if (card) {
      return {
        handle: card.dataset.productHandle,
        title: card.querySelector('.product-card__title, .product-panel__title')?.textContent?.trim(),
        vendor: card.querySelector('.product-card__brand, .product-panel__brand-chip')?.textContent?.trim(),
        price: card.querySelector('.product-card__price')?.textContent?.trim(),
        image: card.querySelector('.product-card__image img, .product-hero__image')?.src,
        url: `/products/${card.dataset.productHandle}`
      };
    }

    // Try sidebar product panel context (desktop)
    const panel = btn.closest('[data-product-panel]');
    if (panel) {
      const brandEl = panel.querySelector('.sp-header__brand');
      const titleEl = panel.querySelector('.sp-title__name');
      const priceEl = panel.querySelector('.sp-retailer--active .sp-retailer__price, .sp-retailer__price');
      const galleryImg = document.querySelector('.product-gallery__image');
      return {
        handle: handle,
        title: titleEl?.textContent?.trim() || '',
        vendor: brandEl?.textContent?.trim() || '',
        price: priceEl?.textContent?.trim() || '',
        image: galleryImg?.src || '',
        url: `/products/${handle}`
      };
    }

    // Try mobile product details context
    const mobileBottom = btn.closest('[data-mobile-bottom]');
    if (mobileBottom) {
      const gallery = document.querySelector('[data-product-gallery]');
      const brandEl = gallery?.querySelector('.mobile-product-details__vendor');
      const titleEl = gallery?.querySelector('.mobile-product-details__full-title');
      const priceEl = mobileBottom.querySelector('.retailer-row--active .retailer-row__price, .retailer-row__price');
      const img = gallery?.querySelector('.product-gallery__image');
      // Extract just the product name (after vendor)
      let title = titleEl?.textContent?.trim() || '';
      const vendorText = brandEl?.textContent?.trim() || '';
      if (title.includes(' - ')) {
        title = title.split(' - ').slice(1).join(' - ').trim();
      }
      return {
        handle: handle,
        title: title,
        vendor: vendorText,
        price: priceEl?.textContent?.trim() || '',
        image: img?.src || '',
        url: `/products/${handle}`
      };
    }

    // Fallback: fetch data from API
    if (handle) {
      fetchAndSaveProductData(handle);
    }

    return null;
  }

  /**
   * Fetch product data from Shopify API and save to wishlist data
   */
  async function fetchAndSaveProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.json`);
      if (!response.ok) return;
      const data = await response.json();
      const product = data.product;
      if (product) {
        saveProductData(handle, {
          handle: handle,
          title: product.title || '',
          vendor: product.vendor || '',
          price: product.variants?.[0]?.price ? `$${product.variants[0].price}` : '',
          image: product.images?.[0]?.src || '',
          url: `/products/${handle}`
        });
      }
    } catch (e) {
      // Silently fail
    }
  }

  /**
   * Update all wishlist count displays
   */
  function updateCounts() {
    const count = getCount();
    const countElements = document.querySelectorAll('[data-wishlist-count]');

    countElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /**
   * Update all button states based on wishlist
   */
  function updateButtonStates() {
    const wishlist = getWishlist();
    const buttons = document.querySelectorAll('[data-wishlist-add]');

    buttons.forEach(btn => {
      const handle = btn.dataset.wishlistAdd;
      const isInList = wishlist.includes(handle);
      updateButtonUI(btn, isInList);
    });
  }

  /**
   * Update single button state
   */
  function updateButtonState(handle, isInList) {
    const buttons = document.querySelectorAll(`[data-wishlist-add="${handle}"]`);
    buttons.forEach(btn => updateButtonUI(btn, isInList));
  }

  /**
   * Update button UI
   */
  function updateButtonUI(btn, isInList) {
    if (isInList) {
      btn.classList.add('btn-add--active');
      btn.classList.add('sp-btn--active');
      btn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        Saved
      `;
    } else {
      btn.classList.remove('btn-add--active');
      btn.classList.remove('sp-btn--active');
      btn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Add
      `;
    }
  }

  /**
   * Show toast notification
   */
  function showToast(message) {
    // Remove existing toast
    const existingToast = document.querySelector('.wishlist-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background-color: var(--color-surface-elevated, #242424);
      color: var(--color-text, #ffffff);
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      z-index: 9999;
      animation: fadeInUp 0.3s ease;
    `;

    document.body.appendChild(toast);

    // Remove after delay
    setTimeout(() => {
      toast.style.animation = 'fadeOutDown 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  /**
   * Dispatch custom event
   */
  function dispatchEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  // Add CSS animations
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translate(-50%, 20px);
      }
      to {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }
    @keyframes fadeOutDown {
      from {
        opacity: 1;
        transform: translate(-50%, 0);
      }
      to {
        opacity: 0;
        transform: translate(-50%, 20px);
      }
    }
  `;
  document.head.appendChild(style);

  // Expose methods globally
  window.Wishlist = {
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist,
    isInWishlist,
    getWishlist,
    getWishlistData,
    getCount,
    clear: clearWishlist,
    bindButtons,
    updateCounts,
    updateButtonStates
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
